import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';
import { TradePair } from '@ui/types/tradePair';
import { getLeverage } from '@ui/utils/trade/getLeverage';
import { getLeverageMargin } from '@ui/utils/trade/getLeverageMargin';
import BigNumber from 'bignumber.js';

/** Small buffer so UI stays below on-chain liquidate check with slippage. */
const LIQUIDATION_MARKET_BUFFER = new BigNumber('1.001');

export const isSafeAfterMarginRemoval = ({
  position,
  pairInfo,
  pairMarketPrice,
  collateralMarketPrice,
  collateralAfterRemoval,
}: {
  position: Position;
  pairInfo: TradePair;
  pairMarketPrice: BigNumber;
  collateralMarketPrice: BigNumber;
  collateralAfterRemoval: BigNumber;
}): boolean => {
  if (collateralAfterRemoval.lte(0) || !pairInfo.feeConfig) {
    return false;
  }

  const collateralUsdAfter = collateralAfterRemoval.times(
    collateralMarketPrice,
  );
  const sizeUsdAtEntry = position.size.times(position.entryPrice);
  const leverageAfter = getLeverage({
    collateralUsd: collateralUsdAfter,
    sizeUsd: sizeUsdAtEntry,
  });

  const liqLostP = getLeverageMargin({
    leverage: leverageAfter.toNumber(),
    leverageMargins: pairInfo.leverageMargins,
  })?.liqLostP;

  if (!liqLostP) {
    return true;
  }

  const liquidationPrice = position.getLiquidationPrice({
    collateralMarketPrice,
    pairMarketPrice,
    feeConfig: pairInfo.feeConfig,
    liqLostP,
    tradePair: pairInfo,
    override: {
      collateralUsd: collateralUsdAfter,
    },
  });

  if (!liquidationPrice) {
    return true;
  }

  if (position.isLong) {
    return pairMarketPrice.gt(liquidationPrice.times(LIQUIDATION_MARKET_BUFFER));
  }

  return pairMarketPrice.lt(
    liquidationPrice.div(LIQUIDATION_MARKET_BUFFER),
  );
};
